export const authorityHubs = {
  humanoidRobots: {
    path: "/humanoid-robots",
    eyebrow: "TechniaHQRobot humanoid robotics hub",
    title: "Humanoid Robots",
    seoTitle: "Humanoid Robots Companies, Models, News and Technology",
    metaDescription:
      "Explore humanoid robots, companies, models, hands, balance, teleoperation, autonomy, safety, software and verified robotics news from TechniaHQRobot.",
    subtitle:
      "A technical guide to biped locomotion, balance, dexterous hands, control methods, deployment evidence and the companies building human-shaped robots.",
    updatedAt: "July 10, 2026",
    intro: [
      "A humanoid robot uses a human-like body plan so it can move through spaces, reach tools and interact with objects designed around people. The shape alone does not determine intelligence. A humanoid may be teleoperated, scripted, remotely assisted or controlled by an autonomous policy, and those modes must be reported separately.",
      "TechniaHQRobot evaluates humanoids as complete physical systems. The useful questions concern foot contact, balance recovery, joint torque, hand force, perception, control frequency, battery life, safety behavior, maintenance access and whether a task can be repeated outside a carefully prepared demonstration.",
    ],
    keyFacts: [
      "A human-shaped body can improve compatibility with stairs, doors, shelves, tools and workstations, but it also creates difficult balance and power constraints.",
      "Smooth movement does not prove autonomous task understanding. Choreography, motion capture, teleoperation and preplanned trajectories can all produce convincing motion.",
      "Hands, wrists and contact sensing often determine whether a humanoid can perform useful work after it reaches the object.",
      "Production deployment requires uptime, maintenance, safety procedures, operator training and integration with the surrounding workflow.",
    ],
    sections: [
      {
        heading: "What qualifies as a humanoid robot",
        paragraphs: [
          "Humanoid robots reproduce part of the human body layout, usually a torso, two arms and a head or sensor module. Some are full bipeds. Others use wheels or a fixed base while keeping human-scale arms and hands. The practical definition should describe the body and intended environment rather than implying human-level reasoning.",
        ],
      },
      {
        heading: "Biped locomotion turns every step into a control problem",
        paragraphs: [
          "Walking requires the robot to estimate body position, predict foot contact and move its center of mass without exceeding friction or joint limits. Encoders, inertial sensors, force sensing and whole-body control work together while the controller updates motor commands. Uneven floors, cables, pushes and uncertain contact make field locomotion harder than a clean laboratory path.",
        ],
      },
      {
        heading: "Balance and fall recovery define practical mobility",
        paragraphs: [
          "A robot may look stable during a planned sequence and still struggle when an object shifts, a foot lands early or a person blocks the route. Useful evaluation includes disturbance rejection, controlled stopping, getting up after a fall, protecting nearby people and detecting when recovery is no longer safe.",
        ],
      },
      {
        heading: "Dexterous hands are the interface to human tools",
        paragraphs: [
          "Human environments contain handles, cables, clothing, dishes, switches and deformable objects. A humanoid hand needs suitable range of motion, grip force, compliance, tactile feedback and durability. High finger count or degrees of freedom do not guarantee reliable manipulation when the object is wet, reflective, flexible or partly hidden.",
        ],
      },
      {
        heading: "Actuators and joint modules set the physical ceiling",
        paragraphs: [
          "Electric motors, reducers, bearings, encoders, torque sensors, brakes, motor drives and cooling determine speed, torque, precision and backdrivability. Compact joints reduce packaging volume but concentrate heat and cabling. A design must balance strength against weight because every heavier limb increases the load carried by the rest of the body.",
        ],
      },
      {
        heading: "Vision and perception must survive changing scenes",
        paragraphs: [
          "Humanoids commonly combine RGB cameras, depth cameras, inertial sensing and joint feedback. Perception software estimates objects, people, free space and grasp geometry. Real deployments introduce glare, darkness, occlusion, transparent surfaces, moving people and unfamiliar object arrangements that are rarely visible in short launch clips.",
        ],
      },
      {
        heading: "Teleoperation is useful, but it is not autonomy",
        paragraphs: [
          "Teleoperation lets a person control the robot through joysticks, tracked hands, motion capture, VR controllers or other interfaces. It can support demonstrations, remote work, exception handling and data collection. The operator still supplies task understanding and judgment, so teleoperated performance should not be presented as autonomous capability.",
        ],
      },
      {
        heading: "Autonomous control connects perception to action",
        paragraphs: [
          "Autonomous behavior requires the robot to interpret a goal, select actions, monitor contact and adapt when the scene differs from the plan. Many systems combine learned policies with conventional state estimation, motion planning, safety limits and human supervision. Autonomy should be described at the task level because navigation, grasping and recovery may use different control methods.",
        ],
      },
      {
        heading: "Industrial humanoids start with constrained workflows",
        paragraphs: [
          "Factories and warehouses offer repeatable tasks, known layouts and measurable cycle times. Early humanoid pilots commonly focus on material movement, part presentation, machine tending and repetitive handling. A pilot becomes a deployment only when the robot can operate across shifts with documented intervention, maintenance and safety procedures.",
        ],
      },
      {
        heading: "Home humanoids face a wider range of objects and risks",
        paragraphs: [
          "Homes contain children, pets, stairs, liquids, clutter and objects that were never modeled by the robot developer. Household usefulness requires safe force, quiet operation, reliable charging, privacy controls, simple recovery and low maintenance. A staged kitchen or laundry demo does not establish dependable unsupervised home operation.",
        ],
      },
      {
        heading: "Healthcare and research platforms require specific evidence",
        paragraphs: [
          "Research humanoids help laboratories study locomotion, manipulation and human-robot interaction. Healthcare use introduces clinical risk, hygiene, accessibility, consent and regulatory requirements. A research demonstration should not be interpreted as an approved medical product without the relevant validation and authorization.",
        ],
      },
      {
        heading: "Safety depends on the complete robot and the task",
        paragraphs: [
          "Safe operation combines mechanical design, speed and force limits, emergency stops, perception coverage, workspace rules, software monitoring and trained human procedures. Risk changes with payload, tool, environment and proximity to people. A robot that is safe during an empty-floor demo may require different safeguards beside workers or patients.",
        ],
      },
      {
        heading: "Current limitations remain mechanical and operational",
        paragraphs: [
          "Battery runtime, thermal limits, hand durability, fall damage, calibration drift, data coverage and recovery behavior remain central constraints. General-purpose language does not remove those limits. The strongest evidence is repeated work under disclosed conditions with clear intervention rates and maintenance records.",
        ],
      },
    ],
    tables: [
      {
        title: "Humanoid robot control methods",
        columns: ["Control method", "Who supplies the task decision", "What the demo can establish", "What remains unknown"],
        rows: [
          ["Teleoperation", "Human operator", "Robot tracking, communications and mechanical response", "Independent task understanding and recovery"],
          ["Preprogrammed motion", "Developer before execution", "Repeatable trajectory execution and balance", "Adaptation to new objects or conditions"],
          ["Motion capture retargeting", "Human performer plus retargeting software", "Whole-body motion reproduction", "Autonomous planning and task reasoning"],
          ["Task autonomy", "Robot policy within a defined task", "Perception-action performance in the tested setup", "Generalization beyond the tested setup"],
        ],
      },
      {
        title: "Major humanoid subsystems",
        columns: ["Subsystem", "Typical hardware or software", "Deployment question"],
        rows: [
          ["Locomotion", "Joint actuators, feet, IMU, force sensing, whole-body control", "Can it recover from imperfect contact and obstacles?"],
          ["Manipulation", "Arms, wrists, hands, tactile and force sensing", "Can it handle the real object without damage or repeated retries?"],
          ["Perception", "RGB, depth, state estimation and object tracking", "Does it work under changing lighting and occlusion?"],
          ["Operations", "Charging, diagnostics, remote assistance and fleet software", "Who responds when the robot stops or needs maintenance?"],
        ],
      },
    ],
    linkGroups: [
      {
        title: "Explore the humanoid robotics ecosystem",
        links: [
          ["Verified global robotics companies directory", "/robotics-companies"],
          ["Humanoid robot companies by continent", "/robotics/humanoid-robot-companies-by-continent"],
          ["Humanoid robots developed in the United States", "/humanoid-robots/united-states"],
          ["Humanoid robots developed in China", "/humanoid-robots/china"],
          ["Humanoid robots developed in Japan", "/humanoid-robots/japan"],
          ["Humanoid robots developed in France", "/humanoid-robots/france"],
        ],
      },
      {
        title: "Hardware, software and current reporting",
        links: [
          ["Robot hands and end effectors", "/robot-components/robot-hands"],
          ["Robot components and hardware", "/robot-components"],
          ["Robotics software and control tools", "/robotics-software"],
          ["Physical AI and robot foundation models", "/physical-ai"],
          ["Robotics data collection", "/robotics-data-collection"],
          ["Latest robotics news", "/robotics-news"],
        ],
      },
    ],
    featuredArticles: [
      ["Boston Dynamics Atlas robots perform Norway’s Viking Row", "/robotics/boston-dynamics-atlas-viking-row"],
      ["Stardust Intelligence humanoid teleoperation explained", "/robotics/stardust-intelligence-humanoid-teleoperation"],
      ["Why humanoid robot hardware remains expensive", "/robotics/humanoid-robot-hardware-cost-drivers"],
      ["What humanoid robot mass production requires", "/robotics/humanoid-robot-mass-production-requirements"],
    ],
    faq: [
      ["Are humanoid robots autonomous?", "Some humanoids perform defined tasks autonomously, while others rely on teleoperation, scripted motion or remote assistance. The control method must be verified for each demonstration."],
      ["Why build a biped instead of a wheeled robot?", "Legs can use stairs and step through human-designed spaces, but wheels are usually more energy efficient and mechanically simpler on flat floors."],
      ["What makes a humanoid robot useful?", "Usefulness depends on repeatable task completion, safe operation, uptime, maintenance and integration with a real workflow rather than body shape alone."],
      ["Do dexterous hands guarantee general manipulation?", "No. Reliable manipulation also requires perception, contact control, task planning, object-specific data and durable hardware."],
      ["How should a humanoid demo be evaluated?", "Check the control method, environment, number of takes, intervention rate, speed, payload, runtime, failure recovery and whether the task was repeated."],
    ],
    sources: [
      ["Boston Dynamics Atlas", "https://bostondynamics.com/atlas/"],
      ["Agility Robotics Digit", "https://www.agilityrobotics.com/solutions/digit/spec-sheet"],
      ["Apptronik Apollo", "https://apptronik.com/our-work/apollo-1"],
      ["1X NEO", "https://www.1x.tech/neo"],
      ["Unitree G1", "https://www.unitree.com/g1"],
      ["PAL Robotics humanoid platforms", "https://pal-robotics.com/robots/"],
    ],
  },
  physicalAI: {
    path: "/physical-ai",
    eyebrow: "TechniaHQRobot Physical AI hub",
    title: "Physical AI",
    seoTitle: "Physical AI Robots, Embodied Intelligence and Real-World AI",
    metaDescription:
      "Learn how Physical AI combines robot perception, foundation models, world models, VLA systems, simulation, demonstrations and closed-loop control.",
    subtitle:
      "A technical map of the data, models, sensors, control loops and evaluation methods required to move AI from digital output into physical action.",
    updatedAt: "August 15, 2026",
    intro: [
      "Physical AI describes AI systems that perceive a physical environment and produce actions through robots, vehicles or other machines. The term covers more than a model. A working system also needs sensors, state estimation, action interfaces, control software, safety limits, compute and a physical platform capable of executing the command.",
      "The central challenge is closed-loop behavior. The machine must observe what happened after an action, compare the result with the goal and adjust. A video prediction can help represent possible futures, but a deployed robot also needs executable actions, timing, contact control and recovery when the world does not match the prediction.",
    ],
    keyFacts: [
      "Generative AI produces digital content; Physical AI must connect perception and decisions to forces, motion and safety constraints in the real world.",
      "Vision-language-action models map observations and instructions to robot actions, but their performance remains tied to the training data, robot embodiment and evaluation task.",
      "Simulation and synthetic data can increase scale, while physical data remains necessary to expose contact, wear, calibration and sensor noise.",
      "Generative action policies need separate evidence for sampling latency, calibrated uncertainty and safe fallback behavior.",
      "Benchmark success does not establish unrestricted autonomy in homes, factories or public spaces.",
      "Physical AI jobs and hiring data are tracked separately so public recruiting signals can be compared without treating job counts as workforce size.",
    ],
    sections: [
      {
        heading: "Physical AI connects machine learning to physical consequences",
        paragraphs: [
          "A text model can generate a sentence without moving matter. A robot action changes contact forces, object position, battery state and risk around people. Physical AI therefore combines model output with geometry, dynamics, timing and control. The same command can produce different results when friction, lighting, payload or object shape changes.",
        ],
      },
      {
        heading: "Embodied AI depends on the robot body",
        paragraphs: [
          "Embodied AI studies intelligence expressed through a body that senses and acts. The body determines reachable space, camera viewpoint, joint limits, gripper geometry and available force. A policy trained on one robot may not transfer directly to another because the observation and action spaces differ.",
        ],
      },
      {
        heading: "Vision-language-action models translate instructions into actions",
        paragraphs: [
          "A VLA model receives visual observations and language, then predicts actions or action tokens. The model may select end-effector motion, joint targets or higher-level skills. Conventional controllers still execute and stabilize the movement, and safety software may reject commands that exceed limits.",
        ],
      },
      {
        heading: "Generative action heads need control-specific evidence",
        paragraphs: [
          "Modern VLA systems may generate continuous action chunks with diffusion or flow matching instead of predicting one deterministic command. This can represent several valid trajectories, but the architecture name does not establish real-time control or safe behavior. Evaluation must include end-to-end latency, replanning, action horizon and physical failure cases.",
          "Policy uncertainty is a separate layer. Sample variation, conformal scores or execution residuals can help decide when to observe again, slow down or request human assistance. Those signals must be calibrated for the stated robot and environment and cannot replace force limits, collision monitoring or emergency stops.",
        ],
      },
      {
        heading: "Robot foundation models seek reusable behavior",
        paragraphs: [
          "Robot foundation models are trained across many tasks, scenes or robot datasets so they can be adapted instead of trained from zero for every behavior. Their useful scope must be measured by the robots, tasks and environments represented in the data. Broad pretraining does not guarantee reliable transfer to a new embodiment or contact-rich task.",
        ],
      },
      {
        heading: "Hiring signals show where Physical AI teams are investing",
        paragraphs: [
          "TechniaHQRobot groups retained Physical AI job listings by company, technology, skill, city and disclosed compensation. A set of world-model listings can show that a company was recruiting in that area at the recorded source date. It does not prove a product launch or total workforce size.",
          "Explore the connected datasets at /jobs, /jobs/physical-ai, /data/physical-ai-hiring-index and /data/physical-ai-skills.",
        ],
      },
      {
        heading: "World models predict how scenes may change",
        paragraphs: [
          "A world model represents possible future states after an action. In robotics, prediction can support planning, synthetic data and evaluation of candidate actions. Prediction quality alone is insufficient because the robot must convert a chosen future into motor commands and verify the result through new observations.",
        ],
      },
      {
        heading: "Perception turns sensor streams into robot state",
        paragraphs: [
          "Cameras, depth sensors, LiDAR, force sensors, tactile arrays, encoders and inertial sensors provide partial measurements. Perception software estimates objects, geometry, motion, contact and robot pose. Errors propagate into planning, so uncertainty and sensor failure must be handled explicitly.",
        ],
      },
      {
        heading: "Manipulation requires contact, not only recognition",
        paragraphs: [
          "Recognizing a cup is different from lifting it without slipping, crushing it or colliding with another object. Manipulation requires grasp geometry, force control, compliance, trajectory planning and feedback after contact. Deformable objects and clutter create additional uncertainty.",
        ],
      },
      {
        heading: "Learning from demonstration captures task examples",
        paragraphs: [
          "Human demonstrations can be recorded through teleoperation, motion capture, kinesthetic teaching or video. A useful dataset aligns observations, actions, timing and task labels. Demonstrations often contain operator habits and coverage gaps, so data quality and task diversity matter as much as quantity.",
        ],
      },
      {
        heading: "Teleoperation produces data and provides human fallback",
        paragraphs: [
          "Teleoperation lets people demonstrate a task and intervene when autonomy fails. Logged camera, joint, action and force data can support imitation learning. Latency, operator skill and interface design influence the recorded behavior, and personal or workplace data requires consent and privacy controls.",
        ],
      },
      {
        heading: "Simulation expands task variation without risking hardware",
        paragraphs: [
          "Simulation can generate many scenes, object positions and failure cases while protecting physical equipment. It supports reinforcement learning, regression testing and synthetic sensor data. The reality gap remains because contacts, materials, lighting, actuator response and wear are approximated.",
        ],
      },
      {
        heading: "Synthetic data needs real-world validation",
        paragraphs: [
          "Synthetic images, trajectories and scenes can fill rare conditions or reduce collection cost. Teams still need physical tests to detect modeling errors and distribution shifts. Synthetic data should be documented so benchmark results do not imply that the same conditions were measured on hardware.",
        ],
      },
      {
        heading: "Closed-loop control repeatedly observes and corrects",
        paragraphs: [
          "In closed-loop control, the robot acts, receives new sensor data and updates the command. Higher inference frequency can reduce the time between observation and correction, but total system response also depends on camera rate, preprocessing, communication, controller timing and actuator dynamics.",
        ],
      },
      {
        heading: "Long-horizon tasks accumulate small errors",
        paragraphs: [
          "A multi-step task may fail even when each individual skill works most of the time. Object displacement, grip error, scene changes and uncertain state accumulate across steps. Long-horizon evaluation should report full-task success, recovery behavior and where human intervention occurred.",
        ],
      },
      {
        heading: "Real-world deployment adds operations and safety",
        paragraphs: [
          "Deployment requires monitoring, updates, permissions, network security, maintenance, charging and procedures for blocked or damaged robots. A model benchmark does not measure these operational requirements. The complete system must be tested in the environment where it will work.",
        ],
      },
      {
        heading: "Physical AI remains bounded by data and hardware",
        paragraphs: [
          "Models can fail on unfamiliar objects, ambiguous instructions and sensor conditions outside the training distribution. Hardware can overheat, lose calibration or reach force and speed limits. Responsible reporting separates model capability from the physical platform and states whether evidence comes from simulation or real robots.",
        ],
      },
    ],
    tables: [
      {
        title: "Physical AI system layers",
        columns: ["Layer", "Inputs", "Output", "Typical failure"],
        rows: [
          ["Perception", "Images, depth, touch and robot state", "Objects, geometry and estimated state", "Occlusion, glare or calibration error"],
          ["Model or planner", "State, instruction and task history", "Action, skill or predicted future", "Distribution shift or ambiguous goal"],
          ["Controller", "Target motion and sensor feedback", "Motor commands", "Timing, instability or limit violation"],
          ["Operations", "Fleet status, alerts and maintenance data", "Human response and scheduling", "Slow intervention or incomplete diagnostics"],
        ],
      },
      {
        title: "Simulation and physical robot evaluation",
        columns: ["Question", "Simulation can test", "Physical testing must confirm"],
        rows: [
          ["Task variation", "Large numbers of layouts and object poses", "Whether the policy transfers to real sensors and contact"],
          ["Safety", "Collision logic and virtual failure cases", "Actual stopping, forces and human procedures"],
          ["Performance", "Policy trends under modeled dynamics", "Runtime, heat, latency, wear and intervention rate"],
          ["Data quality", "Perfect labels and controlled randomization", "Sensor noise, missing data and annotation errors"],
        ],
      },
    ],
    linkGroups: [
      {
        title: "Physical AI intelligence databases",
        links: [
          ["All Robotics & Physical AI Data", "/data"],
          ["Physical AI Model Tracker", "/robotics-news/physical-ai-model-map"],
          ["China Physical AI Model Tracker", "/physical-ai/china-model-tracker"],
          ["Physical AI Benchmark Database", "/data/physical-ai-benchmarks"],
          ["Real-Robot Evidence Database", "/data/physical-ai-real-robot-tests"],
          ["Physical AI Training Data Tracker", "/data/physical-ai-training-data"],
          ["Physical AI World Model Database", "/robotics-news/world-models-for-robotics"],
          ["Cross-Embodiment Model Tracker", "/robotics-news/cross-embodiment-learning"],
          ["Physical AI Inference Latency", "/data/physical-ai-inference-latency"],
          ["Physical AI Deployment Tracker", "/data/physical-ai-deployments"],
          ["Physical AI Failure Database", "/data/physical-ai-failures"],
        ],
      },
      {
        title: "Data, models and control",
        links: [
          ["Robotics data collection", "/robotics-data-collection"],
          ["Robotics software directory", "/robotics-software"],
          ["Robot-agnostic fleet management software", "/robot-management-software"],
          ["Robot sensors and perception hardware", "/robot-sensors"],
          ["Robot components and hardware", "/robot-components"],
          ["Humanoid robots", "/humanoid-robots"],
          ["Flow matching for robot control", "/robotics-news/flow-matching-robot-control"],
          ["Tactile foundation models", "/robotics-news/tactile-foundation-models"],
          ["Robot policy uncertainty and fallbacks", "/robotics-news/robot-policy-uncertainty-estimation"],
        ],
      },
      {
        title: "Current Physical AI reporting",
        links: [
          ["LingBot-VA 2.0 video-action control model", "/robotics/lingbot-va-2-robot-control-foundation-model"],
          ["Perceptron egocentric video annotation", "/robotics/perceptron-egocentric-video-annotation-wgo-bench"],
          ["Stardust humanoid teleoperation", "/robotics/stardust-intelligence-humanoid-teleoperation"],
          ["NVIDIA GR00T robot foundation models", "/ai-news/nvidia-gr00t-robot-foundation-models-physical-ai"],
          ["Latest robotics news", "/robotics-news"],
          ["Robotics research topics", "/robotics-topics"],
        ],
      },
    ],
    featuredArticles: [
      ["LingBot-VA 2.0 combines video prediction and robot actions", "/robotics/lingbot-va-2-robot-control-foundation-model"],
      ["Perceptron segments egocentric robot demonstration videos", "/robotics/perceptron-egocentric-video-annotation-wgo-bench"],
      ["How responsive humanoid teleoperation works", "/robotics/stardust-intelligence-humanoid-teleoperation"],
      ["Robot learning datasets and annotation limits", "/robotics-data-collection"],
      ["Flow-matching robot policies and action chunks", "/robotics-news/flow-matching-robot-control"],
      ["Tactile foundation models for dexterous manipulation", "/robotics-news/tactile-foundation-models"],
      ["Robot policy uncertainty, calibration and fallback", "/robotics-news/robot-policy-uncertainty-estimation"],
    ],
    faq: [
      ["What is Physical AI?", "Physical AI uses sensor data, models and control systems to let machines perceive and act in the physical world."],
      ["How is Physical AI different from generative AI?", "Generative AI produces digital outputs. Physical AI must connect decisions to motion, contact, timing and safety constraints through hardware."],
      ["What is a vision-language-action model?", "A VLA model maps visual observations and language instructions to robot actions or action tokens."],
      ["Why is simulation used for robot learning?", "Simulation provides scalable and safer task variation, but physical testing remains necessary because simulated sensors and contact are approximations."],
      ["Does benchmark success mean a robot is generally autonomous?", "No. Benchmarks measure performance under defined tasks, data and evaluation conditions. Deployment requires broader reliability, safety and operations evidence."],
    ],
    sources: [
      ["NVIDIA What is Physical AI?", "https://www.nvidia.com/en-us/glossary/generative-physical-ai/"],
      ["Google DeepMind RT-2", "https://deepmind.google/blog/rt-2-new-model-translates-vision-and-language-into-action/"],
      ["Open X-Embodiment", "https://robotics-transformer-x.github.io/"],
      ["ROS 2 documentation", "https://docs.ros.org/en/rolling/"],
      ["NVIDIA Isaac Sim documentation", "https://docs.isaacsim.omniverse.nvidia.com/"],
      ["Open-RMF", "https://www.open-rmf.org/"],
    ],
  },
};

export function getAuthorityHubByPath(path) {
  return Object.values(authorityHubs).find((hub) => hub.path === path);
}
